import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Dev-only injection modes used to exercise every QR-validation branch of
 * `/photo-scanning/upload` without printing physical sheets:
 *
 *  - `off`        → no injection (default; production-equivalent path)
 *  - `match`      → overlay the expected `${containerId}-P${seq}` payload
 *  - `mismatch`   → overlay a payload that points at a different container
 *  - `garbage`    → overlay a non-page payload so the backend cannot bind it
 *                   to a slot (drives the `unverified` branch)
 */
export type DevQrInjectMode = 'off' | 'match' | 'mismatch' | 'garbage';

interface DevQrInjectState {
  mode: DevQrInjectMode;
  /**
   * When set, callers must use this as the *starting* sequence number for
   * injection — i.e. the first image gets `containerId-P{override}`, the
   * second `containerId-P{override+1}`, and so on. Lets QA verify that the
   * backend routes uploads by QR rather than by upload order.
   *
   * `null` means "use the natural next sequence" (default behavior).
   */
  sequenceOverride: number | null;
  setMode: (mode: DevQrInjectMode) => void;
  setSequenceOverride: (sequence: number | null) => void;
}

export const useDevQrInjectStore = create<DevQrInjectState>()(
  persist(
    (set) => ({
      mode: 'off',
      sequenceOverride: null,
      setMode: (mode) => set({ mode }),
      setSequenceOverride: (sequenceOverride) => set({ sequenceOverride })
    }),
    { name: 'photo-scanning:dev-qr-inject' }
  )
);
